import { Component, OnInit, Renderer2 } from '@angular/core';
import { FormSelectValues, LaunchContext, SearchCriteria, UserDefaultLaunchContext } from 'src/interfaces/utility-interfaces';
import { NavigationEnd, Router } from '@angular/router';

import { AppConstants } from 'src/constants/constants';
import { FacilityService } from 'src/app/services/facility.service';
import { HttpService } from 'src/app/services/http.service';
import { MyAccountService } from 'src/app/services/my-account.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { StorageService } from 'src/app/services/storage.service';
import { UserPreferenceService } from 'src/app/services/user-preference.service';
import { UtilService } from 'src/app/services/util.service';
import { fhirR4 } from '@smile-cdr/fhirts';

@Component({
  selector: 'app-select-profile',
  templateUrl: './select-profile.component.html',
  styleUrls: ['./select-profile.component.scss']
})
export class SelectProfileComponent implements OnInit {
  hasChanged: boolean;
  warningOpened: boolean;
  leavepage: boolean;
  constructor(
    private oAuthService: OAuthService,
    private myAccountService: MyAccountService,
    private navigationService: NavigationService,
    private utilService: UtilService,
    private renderer2: Renderer2,
    private router: Router,
    private storageService: StorageService,
    private userPreferenceService: UserPreferenceService,
    private httpService: HttpService,
    private facilityService: FacilityService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && event.url.includes(this.oAuthService.getAccessToken())) {
        this.initialRedirect = true;
      }
    });
  }
  selectUndefinedOptionValue: any;
  profilesList: FormSelectValues[] = [];
  organizationMap = {};
  selectedProfileID: string;
  selectedOrgId: string;
  selectedRole: string;
  accessSitesForSelectedOrg = [];
  errorMessage = '';
  showProgressBar = true;
  initialRedirect: boolean;
  showForbiddenError = false;

  ngOnInit() {
    // #2969 when back to profile select page, clear previous selected profile
    sessionStorage.removeItem(StorageService.keyUserOrgId);
    sessionStorage.removeItem(StorageService.keyUserProfileId);
    sessionStorage.removeItem(StorageService.keyUserRole);
    sessionStorage.removeItem(StorageService.keyAccessSites);
    sessionStorage.removeItem('dashboardSearchCriteria');

    this.fetchPractitionerAndRolesUsingUserID();
  }

  fetchPractitionerAndRolesUsingUserID() {
    const identityClaims: any = this.oAuthService.getIdentityClaims();
    this.myAccountService.getUserProfilesAlongWithRelatedData(identityClaims.sub)
    .then(
      (data: fhirR4.Bundle) => {
        if (data.entry && data.entry.length > 0) {
          this.processBundleData(data);
          this.showProgressBar = false;
        } else {
          sessionStorage.setItem('profileError', 'There is an issue with your account. Please contact the Ontario Health - CorHealth Ontario Service Desk.');
          this.showForbiddenErrorAndLogUserOut();
        }
      },
      err => {
        throw err;
      }
    ).catch(error => {
      switch (error.status) {
        case 500: {
          sessionStorage.setItem('profileError', 'There is an issue with your account. Please contact the Ontario Health - CorHealth Ontario Service Desk.');
          break;
        }
        case 403: {
          sessionStorage.setItem('profileError', error.message);
          break;
        }
        default: {
          console.error(error);
        }
      }
      this.showForbiddenErrorAndLogUserOut();
    });
  }

  showForbiddenErrorAndLogUserOut() {
    this.showForbiddenError = true;
    this.navigationService.appLogout(false);
  }

  processBundleData(data: fhirR4.Bundle) {
    this.showProgressBar = true;
    const practitionerRolesList: fhirR4.PractitionerRole[] = [];
    data.entry.forEach((entry: fhirR4.BundleEntry) => {
      switch (entry.resource.resourceType) {
        case 'PractitionerRole':
          const practitionerRole = entry.resource as fhirR4.PractitionerRole;
          practitionerRolesList.push(practitionerRole);
          break;
        case 'Organization':
          const organization = entry.resource as fhirR4.Organization;
          this.organizationMap[organization.id] = organization;
          break;
      }
    });

    if (practitionerRolesList.length === 1) {
      this.showProgressBar = true;
      // If user has only one profile, redirect them to the patients page
      this.selectedProfileID = practitionerRolesList[0].id;
      this.selectedOrgId = this.utilService.getIdFromReference(practitionerRolesList[0].organization.reference);
      this.selectedRole = this.utilService.getCodeFromCodeableConcept(practitionerRolesList[0].code,
        AppConstants.userRoleSystem, 'display');
      this.updateLaunchContext();
      this.saveProfileIDInSession();
      this.setAccessSites(this.selectedOrgId).then(() => {
        this.navigateToDefaultHomePage();
      });
    } else {
      this.processPractitionerRoles(practitionerRolesList);
      this.showProgressBar = false;
    }
  }

  updateLaunchContext() {
    const launchContext: LaunchContext = {
      contextType: 'practitionerrole',
      resourceId: this.selectedProfileID
    };
    const userDefaultLaunchContext: UserDefaultLaunchContext = {
      contexts: [launchContext]
    };
    this.httpService.updateLaunchContext(userDefaultLaunchContext);
  }

  async navigateToDefaultHomePage() {
    try {
      const preference = await this.userPreferenceService.getUserPreferences();
      const pageCode = this.userPreferenceService.parseDefaultHomePageCode(preference);
      const url = this.navigationService.getDefaultHomePageByCode(pageCode);
      this.navigationService.navigateToPage(url);
    } catch (err) {
      this.navigationService.navigateToPatientsPage();
    }
  }

  processPractitionerRoles(practitionerRolesList: fhirR4.PractitionerRole[]) {
    if (practitionerRolesList.length > 0) {
      this.showProgressBar = true;
      practitionerRolesList.forEach((practitionerRole: fhirR4.PractitionerRole) => {
        const organizationID = this.utilService.getIdFromReference(practitionerRole.organization.reference);
        const organization: fhirR4.Organization = this.organizationMap[organizationID];
        const role = this.utilService.getCodeFromCodeableConcept(practitionerRole.code,
          AppConstants.userRoleSystem, 'display');
        const profileListEntry: FormSelectValues = {
          value: practitionerRole.id,
          viewValue: organization.name,
          orgId: organizationID,
          role
        };
        this.profilesList.push(profileListEntry);
      });
      this.profilesList.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
      this.showProgressBar = false;
    }
  }

  saveProfileIDInSession() {
    this.storageService.setStorageItem({ key: 'userOrgID', value: this.selectedOrgId });
    this.storageService.setStorageItem({ key: 'userRole', value: this.selectedRole });
    this.storageService.setStorageItem({ key: 'userProfileID', value: this.selectedProfileID });
    this.storageService.setStorageItem({ key: 'userOrgIdentifier', value: this.fetchIdentifierFromOrganization() });
  }

  fetchIdentifierFromOrganization(): string {
    let retVal = null;
    const organization: fhirR4.Organization = this.organizationMap[this.selectedOrgId];
    for (const identifier of organization.identifier) {
      if (identifier.system === AppConstants.organizationFacilitySystem) {
        retVal = identifier.value;
      }
    }
    return retVal;
  }

  setOrgId(value: string) {
    this.selectedRole = this.profilesList.find(v => v.value === value)?.role;
    this.selectedOrgId = this.profilesList.find(v => v.value === value )?.orgId;
    this.facilityService.getCardiacSitesForProfile( this.selectedOrgId)
      .then(data => {
        this.accessSitesForSelectedOrg = data.map(item =>
          +this.utilService.getIdentifierValueFromResource(item?.resource)
          );
        this.storageService.setStorageItem({key: 'accessSites', value: this.accessSitesForSelectedOrg.join(',')});
      });
    const prevProfileId = sessionStorage.getItem('userProfileID');
    if (this.selectedProfileID === prevProfileId) {
      this.hasChanged = false;
    } else {
      this.hasChanged = true;
    }
  }

  setAccessSites(orgId: string): Promise<void> {
    return this.facilityService.getCardiacSitesForProfile( orgId)
      .then(data => {
        this.accessSitesForSelectedOrg = data.map(item =>
          +this.utilService.getIdentifierValueFromResource(item?.resource)
          );
        this.storageService.setStorageItem({key: 'accessSites', value: this.accessSitesForSelectedOrg.join(',')});
      });
  }

  async selectProfile() {
    this.warningOpened = false;
    this.leavepage = true;
    const buttonElement = document.getElementById('continue');
    this.renderer2.setProperty(buttonElement, 'disabled', true);
    // #3905 bug fix
    const searchCriteria = JSON.parse(sessionStorage.getItem('searchCriteria')) as SearchCriteria;
    let success = true;
    try {
      if (this.selectedProfileID) {
        this.updateLaunchContext();
        this.saveProfileIDInSession();
        if (searchCriteria?.mrn) {
          searchCriteria.mrn = {mrnNumber: null, siteId: null};
          sessionStorage.setItem('searchCriteria', JSON.stringify(searchCriteria));
        }
          await this.setAccessSites(this.selectedOrgId);
          await this.navigateToDefaultHomePage();
      } else {
        this.errorMessage = 'Please select a profile to continue.';
        this.renderer2.removeAttribute(buttonElement, 'disabled');
      }
    } catch (error) {
      console.error('An error occurred:', error);
      this.errorMessage = 'An error occurred while processing your request.';
      success = false;
    } finally {
      if (!success) {
        this.renderer2.removeAttribute(buttonElement, 'disabled');
      }
    }
  }

  onStay(stay: boolean) {
    if (stay) {
      this.navigationService.revertPreviousUrl();
      this.leavepage = false;
      this.warningOpened = false;
    }
  }
}
